import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import type { ArtistProfileBio } from 'fym-shared/src/music24/artistProfile';
import { useDraftArtistBioMutation, useSaveArtistBioMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { studioActionErrorFeedback, type StudioActionFeedback } from '../../studio/studioActionErrorText';
import { profileErrorCode } from '../spotify/spotifyUploadState';
import { bioDraftReducer, bioSaveRefusal, initialBioDraft, isBioDirty, savedTexts } from './bioRules';
import { profileBioMessages as b } from './profileBioMessages';

/**
 * The bio tool (design c1): the two texts, the AI draft that waits for Keep or Discard, Save and
 * the errors. `POST .../bio/draft` saves nothing; `PUT .../bio` saves both texts. A 429 of the
 * Free AI budget or of the 20 drafts a day gets its own sentence.
 */
export function useBioTool(accountId: number, bio: ArtistProfileBio) {
  const intl = useIntl();
  const [state, dispatch] = useReducer(bioDraftReducer, savedTexts(bio), initialBioDraft);
  const [facts, setFacts] = useState('');
  const [feedback, setFeedback] = useState<StudioActionFeedback | null>(null);
  const [savedNow, setSavedNow] = useState(false);
  const [postDraft, draftState] = useDraftArtistBioMutation();
  const [putBio, saveState] = useSaveArtistBioMutation();

  // A new read of the profile (another member saved) replaces the saved text while nothing is edited.
  useEffect(() => {
    dispatch({ type: 'load', saved: { bioShort: bio.bioShort, bioLong: bio.bioLong } });
  }, [bio.bioLong, bio.bioShort]);

  const writeDraft = useCallback(() => {
    setFeedback(null);
    postDraft({ accountId, facts: facts.trim() === '' ? undefined : facts.trim() })
      .then((result) => {
        if (isSuccessfulResult(result)) {
          dispatch({ type: 'draft', texts: { bioShort: result.data.bioShort, bioLong: result.data.bioLong } });
          return;
        }
        const code = profileErrorCode(result.error);
        if (code === 'M24_AI_BUDGET_FREE_EXHAUSTED') {
          setFeedback({ tone: 'danger', message: intl.formatMessage(b.aiBudget) });
        } else if (code === 'M24_PROFILE_BIO_DRAFT_LIMIT') {
          setFeedback({ tone: 'danger', message: intl.formatMessage(b.draftLimit) });
        } else {
          setFeedback(studioActionErrorFeedback(intl, 'profile bio draft', result.error));
        }
      })
      .catch(console.error);
  }, [accountId, facts, intl, postDraft]);

  const refusal = bioSaveRefusal(state.texts);
  const save = useCallback(() => {
    if (bioSaveRefusal(state.texts) !== null) {
      return;
    }
    setFeedback(null);
    setSavedNow(false);
    putBio({ accountId, ...state.texts })
      .then((result) => {
        if (isSuccessfulResult(result)) {
          dispatch({ type: 'saved', texts: savedTexts(result.data.bio) });
          setSavedNow(true);
          return;
        }
        setFeedback(studioActionErrorFeedback(intl, 'profile bio save', result.error));
      })
      .catch(console.error);
  }, [accountId, intl, putBio, state.texts]);

  const edit = useCallback((field: 'bioShort' | 'bioLong', text: string) => {
    setSavedNow(false);
    dispatch({ type: 'edit', field, text });
  }, []);
  const keep = useCallback(() => dispatch({ type: 'keep' }), []);
  const discard = useCallback(() => dispatch({ type: 'discard' }), []);

  return useMemo(
    () => ({
      texts: state.texts,
      isAiDraft: state.isAiDraft,
      isDirty: isBioDirty(state),
      facts,
      setFacts,
      refusal,
      feedback,
      savedNow,
      isDrafting: draftState.isLoading,
      isSaving: saveState.isLoading,
      writeDraft,
      save,
      edit,
      keep,
      discard,
    }),
    [
      discard,
      draftState.isLoading,
      edit,
      facts,
      feedback,
      keep,
      refusal,
      save,
      saveState.isLoading,
      savedNow,
      state,
      writeDraft,
    ]
  );
}
